import { useState } from 'react';
import { X, Settings, Key, Trash2, LogOut, Eye, EyeOff, AlertTriangle, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CSRF token helper
const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

export const AccountSettingsModal = ({ isOpen, onClose, onLogout }) => {
  const [activeSection, setActiveSection] = useState(null); // 'password' | 'delete' | null
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const resetState = () => {
    setActiveSection(null);
    setShowDeleteConfirm(false);
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswords({ old: false, new: false, confirm: false });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Completează toate câmpurile');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Parola nouă trebuie să aibă cel puțin 6 caractere');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Parolele noi nu coincid');
      return;
    }

    setPasswordLoading(true);
    const token = localStorage.getItem('token');
    const csrfToken = getCSRFToken();

    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify({
          old_password: passwordForm.oldPassword,
          new_password: passwordForm.newPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eroare la schimbarea parolei');
      }

      toast.success('Parola a fost schimbată cu succes!');
      resetState();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    const token = localStorage.getItem('token');
    const csrfToken = getCSRFToken();

    try {
      const response = await fetch(`${API_URL}/api/auth/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eroare la ștergerea contului');
      }

      toast.success('Contul a fost șters cu succes');
      handleClose();
      onLogout();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    // Only close if clicking directly on the backdrop, not on children
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <>
      {/* Backdrop - fixed to viewport */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 9998 }}
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Modal container - using grid for perfect centering */}
      <div 
        className="fixed grid place-items-center p-4"
        style={{ 
          zIndex: 9999, 
          pointerEvents: 'none',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh'
        }}
      >
        {/* Modal */}
        <div 
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
          style={{ pointerEvents: 'auto' }}
          data-testid="account-settings-modal"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div 
          className="px-6 py-4 border-b border-gray-100"
          style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1a3a5c 100%)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10"
              >
                <Settings className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Setările Contului</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              data-testid="close-account-settings-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Change Password Section */}
          <div className="border-2 border-gray-100 rounded-xl overflow-hidden">
            <button
              onClick={() => setActiveSection(activeSection === 'password' ? null : 'password')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-all"
              data-testid="change-password-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Key className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-navy">Schimbă parola</p>
                  <p className="text-sm text-gray-500">Actualizează parola contului</p>
                </div>
              </div>
              <div className={`transform transition-transform ${activeSection === 'password' ? 'rotate-180' : ''}`}>
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            
            {/* Password Form */}
            {activeSection === 'password' && (
              <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-4 animate-fade-in">
                {/* Old Password */}
                <div className="relative">
                  <input
                    type={showPasswords.old ? 'text' : 'password'}
                    value={passwordForm.oldPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-navy"
                    placeholder="Parola curentă"
                    data-testid="old-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, old: !showPasswords.old })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.old ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* New Password */}
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-navy"
                    placeholder="Parola nouă (min. 6 caractere)"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Confirm Password */}
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all text-navy"
                    placeholder="Confirmă parola nouă"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                <Button
                  onClick={handleChangePassword}
                  disabled={passwordLoading}
                  className="w-full py-3"
                  style={{ 
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                    color: 'white'
                  }}
                  data-testid="save-password-btn"
                >
                  {passwordLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Salvează noua parolă
                </Button>
              </div>
            )}
          </div>

          {/* Delete Account Section */}
          <div className="border-2 border-red-100 rounded-xl overflow-hidden">
            <button
              onClick={() => setActiveSection(activeSection === 'delete' ? null : 'delete')}
              className="w-full flex items-center justify-between p-4 hover:bg-red-50 transition-all"
              data-testid="delete-account-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-red-600">Șterge contul</p>
                  <p className="text-sm text-gray-500">Acțiune permanentă și ireversibilă</p>
                </div>
              </div>
              <div className={`transform transition-transform ${activeSection === 'delete' ? 'rotate-180' : ''}`}>
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            
            {/* Delete Confirmation */}
            {activeSection === 'delete' && (
              <div className="px-4 pb-4 border-t border-red-100 pt-4 animate-fade-in">
                {!showDeleteConfirm ? (
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                    <p className="text-sm text-gray-700 mb-4">
                      Această acțiune va șterge permanent contul tău și toate datele asociate.
                    </p>
                    <Button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="bg-red-500 hover:bg-red-600 text-white w-full"
                      data-testid="confirm-delete-step1-btn"
                    >
                      Doresc să îmi șterg contul
                    </Button>
                  </div>
                ) : (
                  <div className="bg-red-100 rounded-lg p-4 text-center animate-fade-in">
                    <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                    <p className="font-bold text-red-700 mb-2">Sunteți sigur?</p>
                    <p className="text-sm text-red-600 mb-4">
                      Această acțiune este ireversibilă și va șterge toate datele asociate contului tău.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowDeleteConfirm(false)}
                        variant="outline"
                        className="flex-1 border-gray-300"
                        data-testid="cancel-delete-btn"
                      >
                        Anulează
                      </Button>
                      <Button
                        onClick={handleDeleteAccount}
                        disabled={deleteLoading}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="confirm-delete-final-btn"
                      >
                        {deleteLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Șterge definitiv
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Logout Button */}
          <button
            onClick={() => {
              handleClose();
              onLogout();
            }}
            className="w-full flex items-center justify-center gap-3 p-4 border-2 border-red-200 rounded-xl text-red-600 hover:bg-red-50 transition-all font-semibold"
            data-testid="logout-from-settings-btn"
          >
            <LogOut className="w-5 h-5" />
            Deconectare
          </button>
        </div>
      </div>
      </div>
    </>
  );
};
